import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useLazyQuery, useLazyCommand, useEvent } from '@soap/modules';
import { CheckCircle } from 'lucide-react';
import { useSelectedStudent } from '../hooks/useSelectedStudent';
import { RichTextEditor } from './RichTextEditor';
import { LYW_BRAND } from '../constants/brandPalette';

interface ShareByEmailDialogProps {
  assessmentId: string;
  studentId?: string;
  unitId?: string;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export function ShareByEmailDialog({ assessmentId, studentId, unitId, open, onClose, onSuccess }: ShareByEmailDialogProps) {
  const [viewState, setViewState] = useState<'form' | 'success'>('form');
  
  // Email form state
  const [emailSubject, setEmailSubject] = useState('');
  const [emailText, setEmailText] = useState('');
  const [emailExpiry, setEmailExpiry] = useState('');
  
  const { selectedStudentId, students } = useSelectedStudent();
  
  // Use selectedStudentId from hook, or fallback to prop studentId
  const targetStudentId = selectedStudentId || studentId;
  const selectedStudent = targetStudentId ? students.find(s => s.id === targetStudentId) : null;
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  // Get form data queries
  const [getEmailFormData] = useLazyQuery("C200v1_GetC199FormData", { C200_AssessmentId: assessmentId });
  
  // Commands
  const [shareByEmail] = useLazyCommand("C199v1_ShareByEmail");
  
  // Listen for form data events
  useEvent({
    eventName: "E161v1_GotC199FormData",
    onEventReceived: (event: any) => {
      // UIFormDataEvent uses e000_FieldData array, not direct properties
      const fieldData = event.e000_FieldData || [];
      
      // Find the C199 fields (field names are lowercase with underscore)
      const emailSubjectField = fieldData.find((field: any) => field.name === 'c199_EmailSubject');
      const emailTextField = fieldData.find((field: any) => field.name === 'c199_EmailText');
      
      if (emailSubjectField?.initialValue) {
        setEmailSubject(emailSubjectField.initialValue);
      }
      if (emailTextField?.initialValue) {
        setEmailText(emailTextField.initialValue);
      }
    }
  });
  
  
  // Listen for success events
  useEvent({
    eventName: "E166v1_AssessmentShared",
    onEventReceived: (event: any) => {
      if (onSuccess) {
        onSuccess();
      }
      setViewState('success');
    }
  });
  
  useEffect(() => {
    if (open) {
      setViewState('form');
      // Load form data when dialog opens
      getEmailFormData();
    }
  }, [open]);
  
  const resetForm = () => {
    setViewState('form');
    setEmailSubject('');
    setEmailText('');
    setEmailExpiry('');
    setErrors({});
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };
  
  const validateEmailForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!targetStudentId) {
      newErrors.selectedStudent = 'Please select a student from the dropdown in the upper right corner';
    } else if (!selectedStudent?.email) {
      newErrors.selectedStudent = 'The selected student does not have an email address. Please add an email address to the student first.';
    }
    if (!emailSubject.trim()) {
      newErrors.emailSubject = 'Email subject is required';
    }
    if (!emailText.trim()) {
      newErrors.emailText = 'Email text is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Get selected student name
  const selectedStudentName = selectedStudent?.name || '';

  const emailGreeting = `Hi${selectedStudentName ? ` ${selectedStudentName},` : ','}`;
  const suffixStart = "Please ";
  const suffixLink = "click here";
  const suffixEnd = " to take the assessment.";
  
  const handleSubmit = async () => {
    if (!validateEmailForm()) return;
    await shareByEmail({
      C199_AssessmentId: assessmentId,
      C199_UnitId: unitId,
      C199_StudentId: targetStudentId || null,
      C199_EmailSubject: emailSubject,
      C199_EmailText: emailText,
      C199_Expiry: emailExpiry ? new Date(emailExpiry).toISOString() : null
    });
  };
  
  if (viewState === 'success') {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-w-2xl">
          <div className="flex flex-col items-center justify-center py-8">
            <CheckCircle className="h-12 w-12 text-confirm-action mb-4" />
            <DialogTitle className="text-center">Assessment Shared Successfully</DialogTitle>
            <DialogDescription className="text-center mt-2">
              The assessment has been shared successfully via email.
            </DialogDescription>
            
            <div className="mt-6">
              <Button onClick={handleClose}>Close</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }
  
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Share Assessment by Email</DialogTitle>
          <DialogDescription>
            Send the assessment via email to one or more recipients
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {errors.selectedStudent && (
            <div className="p-3 bg-destructive-action/10 border border-destructive-action/30 rounded-lg">
              <p className="text-sm text-destructive-action">{errors.selectedStudent}</p>
            </div>
          )}
          
          {selectedStudent && (
            <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <Label className="text-sm font-medium text-gray-700">Sending to:</Label>
              <p className="text-sm text-gray-900 mt-1">{selectedStudent.name}</p>
              {selectedStudent.email && (
                <p className="text-xs text-gray-500 mt-1">{selectedStudent.email}</p>
              )}
            </div>
          )}
          
          <div>
            <Label htmlFor="emailSubject">Email Subject</Label>
            <Input
              id="emailSubject"
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              className={errors.emailSubject ? 'border-destructive-action' : ''}
            />
            {errors.emailSubject && <p className="text-sm text-destructive-action mt-1">{errors.emailSubject}</p>}
          </div>
          
          <div>
            <Label htmlFor="emailText">Email Text</Label>
            <div className="bg-gray-50 border border-gray-200 rounded-lg w-full overflow-hidden">
              <div className="text-sm text-gray-700 px-4 pt-4 pb-2">
                <p>{emailGreeting}</p>
              </div>
              <div className="px-0">
                <RichTextEditor
                  value={emailText}
                  onChange={setEmailText}
                  height={200}
                  error={!!errors.emailText}
                  className="seamless-editor"
                  buttonPreset="limited-text-only"
                  previewTemplate={`<div style="font-family: sans-serif; font-size: 14px; color: #374151;"><p>${emailGreeting}</p></div>{{CONTENT}}<div style="font-family: sans-serif; font-size: 14px; color: #374151; margin-top: 10px;"><p>${suffixStart}<span style="font-weight: 600; color: ${LYW_BRAND.actionBlue};">${suffixLink}</span>${suffixEnd}</p></div>`}
                />
              </div>
              {errors.emailText && <p className="text-sm text-destructive-action mt-1 px-4">{errors.emailText}</p>}
              <div className="px-4 pb-4 pt-2">
                <div className="text-left text-sm text-gray-700 w-full">
                  <p>{suffixStart}<span className="font-semibold text-action-blue mt-2">{suffixLink}</span>{suffixEnd}</p>
                </div>
              </div>
              <style>{`
                .seamless-editor .jodit-container {
                  border: none !important;
                  border-radius: 0 !important;
                  background: transparent !important;
                }
                .seamless-editor .jodit-container:focus-within {
                  border: none !important;
                  box-shadow: none !important;
                }
                .seamless-editor .jodit-toolbar-editor-collection {
                  border-top-left-radius: 0 !important;
                  border-top-right-radius: 0 !important;
                  border-left: none !important;
                  border-right: none !important;
                  border-top: none !important;
                }
                .seamless-editor .jodit-wysiwyg {
                  border: none !important;
                }
              `}</style>
            </div>
          </div>
          
          <div>
            <Label htmlFor="emailExpiry">Expiration Date (optional)</Label>
            <Input
              id="emailExpiry"
              type="datetime-local"
              value={emailExpiry}
              onChange={(e) => setEmailExpiry(e.target.value)}
            />
            <p className="text-sm text-gray-500 mt-1">After this date the link in the email will be invalid</p>
          </div>
        </div>
        
        <div className="flex justify-end space-x-2">
          <Button variant="outline" onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit}>Send Email</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
